/// <reference types="@webgpu/types" />

export class AnomalyMediaPlayer {
    private canvas: HTMLCanvasElement
    private device?: GPUDevice
    private context?: GPUCanvasContext
    private videoPipeline?: GPURenderPipeline
    private imagePipeline?: GPURenderPipeline
    private imageTexture?: GPUTexture
    private sampler?: GPUSampler

    private anomalyBuffer?: GPUBuffer
    private sensitivityBuffer?: GPUBuffer
    private opacityBuffer?: GPUBuffer

    private anomalyIndex: Uint8Array

    constructor(canvas: HTMLCanvasElement, anomalyIndex: Uint8Array) {
        this.canvas = canvas

        if (anomalyIndex.byteLength !== 32768) {
            throw new Error(
                `Invalid anomalyIndex size: ${anomalyIndex.byteLength}. Expected 32768.`
            )
        }
        this.anomalyIndex = anomalyIndex
    }

    public async initialize(): Promise<void> {
        if (!navigator.gpu) {
            throw new Error("WebGPU is not supported on this browser.")
        }

        const adapter = await navigator.gpu.requestAdapter()
        if (!adapter) {
            throw new Error("Failed to get GPU adapter.")
        }
        this.device = await adapter.requestDevice({
            requiredLimits: {
                maxTextureDimension2D: Math.min(adapter.limits.maxTextureDimension2D, 16384),
            },
        })

        this.anomalyBuffer = this.device.createBuffer({
            size: 32768,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        })

        const packedData = new Uint32Array(8192)
        const byteView = new Uint8Array(packedData.buffer)
        byteView.set(this.anomalyIndex)
        this.device.queue.writeBuffer(this.anomalyBuffer, 0, packedData)

        this.sensitivityBuffer = this.device.createBuffer({
            size: 4,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        })

        this.opacityBuffer = this.device.createBuffer({
            size: 4,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        })

        this.setSensitivity(75)
        this.setFilterOpacity(62.5)

        const context = this.canvas.getContext("webgpu")
        if (!context) {
            throw new Error("Failed to get WebGPU context.")
        }
        this.context = context
        const presentationFormat = navigator.gpu.getPreferredCanvasFormat()

        this.context.configure({
            device: this.device,
            format: presentationFormat,
            alphaMode: "opaque",
        })

        const vertexShaderCode = `
          struct VertexOutput {
            @builtin(position) position: vec4<f32>,
            @location(0) tex_coord: vec2<f32>,
          };
    
          @vertex
          fn vs_main(@builtin(vertex_index) in_vertex_index: u32) -> VertexOutput {
            var output: VertexOutput;
            let x = f32(in_vertex_index / 2u) * 4.0 - 1.0;
            let y = f32(in_vertex_index % 2u) * 4.0 - 1.0;
            output.position = vec4<f32>(x, -y, 0.0, 1.0);
            output.tex_coord = vec2<f32>((x + 1.0) / 2.0, (y + 1.0) / 2.0);
            return output;
          }
        `
        const fragmentShaderLogic = `
        @group(0) @binding(2) var<storage, read> anomaly_index_packed: array<u32>;
        @group(0) @binding(3) var<uniform> sensitivity: u32;
        @group(0) @binding(4) var<uniform> opacity: f32;
      
        fn get_global_score(c: vec4<f32>) -> f32 {
            let color_u32 = vec4<u32>(c * 255.0);
            let r = color_u32.r >> 3u;
            let g = color_u32.g >> 3u;
            let b = color_u32.b >> 3u;
            let color_id = r * 1024u + g * 32u + b;
            
            let packed = anomaly_index_packed[color_id / 4u];
            let shift = (color_id % 4u) * 8u;
            return f32((packed >> shift) & 0xFFu); 
        }
      
        fn resolve_anomaly_color(original: vec4<f32>, global_score: f32) -> vec4<f32> {
            let sens_f = f32(sensitivity);
            
            let is_common = step(sens_f, global_score);
      
            let color_safe   = vec3<f32>(0.5, 0.5, 0.84);   // Blue
            let color_danger = vec3<f32>(0.95, 0.66, 0.1);  // Orange
      
            let selected_tint = mix(color_danger, color_safe, is_common);
      
            let filtered_rgb = original.rgb * selected_tint;
            let final_rgb = mix(original.rgb, filtered_rgb, opacity * 2.0);
            
            return vec4<f32>(final_rgb, original.a);
        }
      `

        const videoShaderModule = this.device.createShaderModule({
            code: `
        ${vertexShaderCode}
        ${fragmentShaderLogic}
    
        @group(0) @binding(0) var my_sampler: sampler;
        @group(0) @binding(1) var my_texture: texture_external;
        
        @fragment
        fn fs_main(@location(0) tex_coord: vec2<f32>) -> @location(0) vec4<f32> {
            // 1. Sample Center
            let center = textureSampleBaseClampToEdge(my_texture, my_sampler, tex_coord);
            
            // 2. Get Score
            let global_score = get_global_score(center);
    
            // 3. Resolve (Passing only 2 arguments)
            return resolve_anomaly_color(center, global_score);
        }
      `,
        })
        this.videoPipeline = this.device.createRenderPipeline({
            layout: "auto",
            vertex: { module: videoShaderModule, entryPoint: "vs_main" },
            fragment: {
                module: videoShaderModule,
                entryPoint: "fs_main",
                targets: [{ format: presentationFormat }],
            },
            primitive: { topology: "triangle-strip" },
        })

        const imageShaderModule = this.device.createShaderModule({
            code: `
            ${vertexShaderCode}
            ${fragmentShaderLogic}
        
            @group(0) @binding(0) var my_sampler: sampler;
            @group(0) @binding(1) var my_texture: texture_2d<f32>;
            
            @fragment
            fn fs_main(@location(0) tex_coord: vec2<f32>) -> @location(0) vec4<f32> {
                let center = textureSampleLevel(my_texture, my_sampler, tex_coord, 0.0);
                let global_score = get_global_score(center);
        
                return resolve_anomaly_color(center, global_score);
            }
          `,
        })

        this.imagePipeline = this.device.createRenderPipeline({
            layout: "auto",
            vertex: { module: imageShaderModule, entryPoint: "vs_main" },
            fragment: {
                module: imageShaderModule,
                entryPoint: "fs_main",
                targets: [{ format: presentationFormat }],
            },
            primitive: { topology: "triangle-strip" },
        })

        this.sampler = this.device.createSampler({
            magFilter: "nearest",
            minFilter: "nearest",
        })
    }

    public setSensitivity(sensitivity: number): void {
        if (!this.device || !this.sensitivityBuffer) {
            return
        }

        const ranged = (sensitivity / 300 + 0.66) * 100

        const s = Math.max(0, Math.min(100, ranged))

        const threshold = Math.floor((100 - s) * 2.55)

        this.device.queue.writeBuffer(this.sensitivityBuffer, 0, new Uint32Array([threshold]))
    }

    public setFilterOpacity(opacity: number): void {
        if (!this.device || !this.opacityBuffer) {
            return
        }
        const cleanOpacity = Math.max(0, Math.min(100, opacity)) / 100.0
        this.device.queue.writeBuffer(this.opacityBuffer, 0, new Float32Array([cleanOpacity]))
    }

    private _performRenderPass(pipeline: GPURenderPipeline, bindGroup: GPUBindGroup): void {
        if (!this.device || !this.context) return

        const commandEncoder = this.device.createCommandEncoder()
        const renderView = this.context.getCurrentTexture().createView()

        const passEncoder = commandEncoder.beginRenderPass({
            colorAttachments: [
                {
                    view: renderView,
                    clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 },
                    loadOp: "clear",
                    storeOp: "store",
                },
            ],
        })

        passEncoder.setPipeline(pipeline)
        passEncoder.setBindGroup(0, bindGroup)
        passEncoder.draw(4)
        passEncoder.end()

        this.device.queue.submit([commandEncoder.finish()])
    }

    public renderVideoFrame(source: VideoFrame | HTMLVideoElement): void {
        if (
            !this.device ||
            !this.videoPipeline ||
            !this.sampler ||
            !this.anomalyBuffer ||
            !this.sensitivityBuffer ||
            !this.opacityBuffer
        ) {
            console.error("AnomalyMediaPlayer not fully initialized for video.")
            return
        }

        const sourceTexture = this.device.importExternalTexture({ source })

        const bindGroup = this.device.createBindGroup({
            layout: this.videoPipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: this.sampler },
                { binding: 1, resource: sourceTexture },
                { binding: 2, resource: { buffer: this.anomalyBuffer } },
                { binding: 3, resource: { buffer: this.sensitivityBuffer } },
                { binding: 4, resource: { buffer: this.opacityBuffer } },
            ],
        })

        this._performRenderPass(this.videoPipeline, bindGroup)
    }

    public renderImage(source: ImageBitmap): void {
        if (
            !this.device ||
            !this.imagePipeline ||
            !this.sampler ||
            !this.anomalyBuffer ||
            !this.sensitivityBuffer ||
            !this.opacityBuffer
        ) {
            console.error("AnomalyMediaPlayer not fully initialized for images.")
            return
        }

        if (
            !this.imageTexture ||
            this.imageTexture.width !== source.width ||
            this.imageTexture.height !== source.height
        ) {
            if (this.imageTexture) {
                this.imageTexture.destroy()
            }
            this.imageTexture = this.device.createTexture({
                size: [source.width, source.height, 1],
                format: "rgba8unorm",
                usage:
                    GPUTextureUsage.TEXTURE_BINDING |
                    GPUTextureUsage.COPY_DST |
                    GPUTextureUsage.RENDER_ATTACHMENT,
            })
        }

        this.device.queue.copyExternalImageToTexture({ source }, { texture: this.imageTexture }, [
            source.width,
            source.height,
        ])

        const bindGroup = this.device.createBindGroup({
            layout: this.imagePipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: this.sampler },
                { binding: 1, resource: this.imageTexture.createView() },
                { binding: 2, resource: { buffer: this.anomalyBuffer } },
                { binding: 3, resource: { buffer: this.sensitivityBuffer } },
                { binding: 4, resource: { buffer: this.opacityBuffer } },
            ],
        })
        this._performRenderPass(this.imagePipeline, bindGroup)
    }
}
